import { getCurrentInstance, onUnmounted } from 'vue';
import Fuse from 'fuse.js';
import { FuseWorker } from 'fuse.js/worker';
import type { IFuseOptions, FuseOptionKey } from 'fuse.js';

/**
 * Whether the browser global `Worker` is defined.
 * Note: This check does NOT guarantee Web Workers will work at runtime.
 * CSP restrictions, worker script resolution failures, or other runtime errors
 * may still occur and are handled with try/catch fallback to synchronous Fuse.
 */
const isWorkerAvailable = typeof Worker !== 'undefined';

/**
 * Composable providing async, non-blocking fuzzy search via FuseWorker
 * (parallel Web Workers).
 *
 * Manages a single persistent FuseWorker instance per component lifetime:
 * - First call -> creates a FuseWorker with the given collection and keys.
 * - Same keys, new collection -> calls setCollection() to reshard without recreating worker threads.
 * - Same collection and keys, new keyword -> calls search() directly on already-warm workers.
 * - Keys changed -> terminates the existing instance and creates a fresh one.
 *
 * Falls back to synchronous `Fuse` when Web Workers are
 * unavailable.
 */
export function useFuseWorker() {
  let worker: FuseWorker<unknown> | null = null;
  let lastCollection: unknown[] | null = null;
  let lastKeysSignature: string | null = null;

  const defaultOptions: Partial<IFuseOptions<unknown>> = {
    findAllMatches: true,
    includeScore: true,
    ignoreLocation: true, // Don't care about position in string
    threshold: 0.35, // Empirically chosen balance between strict and fuzzy matches
    distance: 100, // Allow some distance for typo tolerance
    minMatchCharLength: 1, // Require at least 1 character
    includeMatches: true, // Helpful for debugging what matched
    isCaseSensitive: false, // Handle case variations
    shouldSort: true // Sort results by relevance score
  };

  /**
   * Performs a synchronous fuzzy search over the given collection using Fuse.js.
   * Used as a fallback when Web Workers are unavailable or fail at runtime.
   * @param collection Pre-filtered list of items to search over.
   * @param keyword Search term.
   * @param options Partial Fuse options to override defaults.
   * @returns Matched items in relevance order (scores stripped).
   */
  function searchWithFuseFallback<T>(
    collection: T[],
    keyword: string,
    options?: Partial<IFuseOptions<T>>
  ): T[] {
    const fuse = new Fuse(collection, options);
    return fuse.search(keyword).map(({ item }) => item);
  }

  /**
   * Performs an async fuzzy search over the given collection.
   *
   * Reuses the internal FuseWorker instance when the collection and keys are
   * unchanged (typical for search-as-you-type), calling setCollection() only
   * when the pre-filtered collection reference changes.
   *
   * @param collection Pre-filtered list of items to search over.
   * @param keys Fuse.js key paths to search within each item.
   * @param keyword Search term.
   * @param options Partial Fuse options to override defaults.
   * @returns Matched items in relevance order (scores stripped).
   */
  async function search<T>(
    collection: T[],
    keys: FuseOptionKey<T>[],
    keyword: string,
    options?: Partial<IFuseOptions<T>>
  ): Promise<T[]> {
    const mergedOptions = {
      keys,
      ...defaultOptions,
      ...options
    } as IFuseOptions<T>;

    // Fallback when Web Workers are unavailable use synchronous Fuse search.
    if (!isWorkerAvailable) {
      return searchWithFuseFallback(collection, keyword, mergedOptions);
    }

    const keysSignature = JSON.stringify(keys);
    const collectionChanged = lastCollection !== collection;
    const keysChanged = lastKeysSignature !== keysSignature;

    if (!worker || keysChanged) {
      // First call or keys changed — create a fresh worker instance.
      // FuseWorker spawns Web Worker threads lazily on the first search() call,
      // so construction here is cheap.
      //
      // If the worker script fails to auto-resolve (some bundler configs), pass
      // the URL explicitly:
      //   import workerUrl from 'fuse.js/worker-script'
      //   worker = new FuseWorker(collection, mergedOptions, { workerUrl })
      try {
        worker?.terminate();
        // Vue reactive proxies cannot be transferred via postMessage (structured clone).
        // JSON round-trip converts them to plain objects before handing off to the worker.
        const plainCollection = JSON.parse(JSON.stringify(collection)) as unknown[];
        worker = new FuseWorker(
          plainCollection,
          mergedOptions as IFuseOptions<unknown>
        );
        lastCollection = collection as unknown[];
        lastKeysSignature = keysSignature;
      } catch (err) {
        // Worker creation failed (CSP, resolution error, etc.) — fall back to sync Fuse.
        console.warn('FuseWorker creation failed, falling back to synchronous Fuse:', err);
        worker = null;

        // Fall back to synchronous Fuse search with the new collection and same keys.
        return searchWithFuseFallback(collection, keyword, mergedOptions);
      }
    } else if (collectionChanged) {
      // Same keys, new collection (e.g. active filters changed) — replace the
      // dataset across all worker shards without recreating worker threads.
      try {
        const plainCollection = JSON.parse(JSON.stringify(collection)) as unknown[];
        await worker.setCollection(plainCollection);
        lastCollection = collection as unknown[];
      } catch (err) {
        // setCollection failed — fall back to sync Fuse.
        console.warn('FuseWorker.setCollection() failed, falling back to synchronous Fuse:', err);
        worker = null;

        // Fall back to synchronous Fuse search with the new collection and same keys.
        return searchWithFuseFallback(collection, keyword, mergedOptions);
      }
    }

    // Same collection and keys → call search() directly on warm workers.
    try {
      const results = await worker.search(keyword);
      return results.map(({ item }) => item as T);
    } catch (err) {
      // Worker search failed — fall back to sync Fuse.
      console.warn('FuseWorker.search() failed, falling back to synchronous Fuse:', err);
      worker = null;

      // Fall back to synchronous Fuse search with the new collection and same keys.
      return searchWithFuseFallback(collection, keyword, mergedOptions);
    }
  }

  /**
   * Terminates all Web Worker threads and resets internal state.
   * Called automatically on component unmount when used inside a setup context.
   */
  function terminate() {
    worker?.terminate();
    worker = null;
    lastCollection = null;
    lastKeysSignature = null;
  }

  // Register automatic cleanup only when called inside a Vue component setup context.
  // Guarded to allow safe use in utility functions and test helpers outside a component.
  if (getCurrentInstance()) {
    onUnmounted(terminate);
  }

  return { search, terminate, isWorkerAvailable };
}
